import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, setToken, clearToken } from '../api/client';

export type LoginCredentials = string | { email?: string; password?: string; token?: string };

interface Tenant { id: string; name: string; email?: string; plan: 'free' | 'pro' | 'enterprise'; storage_used_bytes: number; role: 'admin' | 'user'; tokens_balance: number; shared_token_key?: string; parent_tenant_id?: string; }
interface AuthCtx {
  tenant: Tenant | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.me();
      setTenant(res.tenant);
    } catch (e) {
      clearToken();
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('erixone_token') ?? localStorage.getItem('ericone_token');
    if (!token) { setLoading(false); return; }
    fetchProfile();
  }, [fetchProfile]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setLoading(true);
    try {
      const res = await api.login(credentials);
      setToken(res.token);
      setTenant(res.tenant);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      throw e;
    }
  }, []);

  const logout = useCallback(() => {
    api.logout().catch(() => {});
    clearToken();
    setTenant(null);
  }, []);

  return (
    <AuthContext.Provider value={{ tenant, isLoggedIn: !!tenant, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
